import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import axios from "axios";
import toast from "react-hot-toast";
import styles from "./EmployeeProfile.module.css";
import {
  FaArrowLeft,
  FaFileInvoiceDollar,
  FaPlus,
  FaBriefcase,
  FaEnvelope,
  FaPen,
  FaTrash,
} from "react-icons/fa";
import { DeleteModal } from "../Fields/FieldDetails";

const TransactionItem = ({ transaction, isRTL, onEdit, onDelete }) => {
  const { t, i18n } = useTranslation();

  const getTransactionDetails = (type) => {
    switch (type) {
      case "salaryPayment":
        return {
          title: t("Salary Payment"),
          sign: "+",
          styleClass: styles.amountPositive,
        };
      case "salaryAdvance":
        return {
          title: t("Advance from Salary"),
          sign: "+",
          styleClass: styles.amountPositive,
        };
      case "salaryDeduction":
        return {
          title: t("Deduction from Salary"),
          sign: "-",
          styleClass: styles.amountNegative,
        };
      default:
        return { title: type, sign: "", styleClass: "" };
    }
  };

  const { title, sign, styleClass } = getTransactionDetails(
    transaction.payment.type
  );
  const formattedDate = new Date(
    transaction.payment.paymentDate
  ).toLocaleDateString(i18n.language === "ar" ? "ar-EG" : "en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className={styles.transactionItem}>
      <div className={styles.transactionDetails}>
        <h3>{title}</h3>
        <p>
          {transaction.payment.description}
          <br />
          <span className={styles.transactionDate}>{formattedDate}</span>
        </p>
      </div>

      <div className={styles.transactionAmountAndActions}>
        <span className={styleClass}>
          {sign}{" "}
          {transaction.payment.amountPaid?.toLocaleString(i18n.language) ||
            "0.00"}
        </span>
        <div className={styles.transactionActions}>
          <button
            className={styles.actionButton}
            aria-label={t("Edit")}
            onClick={() => onEdit(transaction)}
          >
            <FaPen />
          </button>
          <button
            className={`${styles.actionButton} ${styles.deleteButton}`}
            aria-label={t("Delete")}
            onClick={() => onDelete(transaction)}
          >
            <FaTrash />
          </button>
        </div>
      </div>
    </div>
  );
};

const EmployeeProfile = () => {
  const { employeeId } = useParams();
  const { t, i18n } = useTranslation();
  const { token, userData } = useContext(UserContext);
  const navigate = useNavigate();
  const monthSliderRef = useRef(null);

  const [employee, setEmployee] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [months, setMonths] = useState([]);
  const [activeMonth, setActiveMonth] = useState(new Date().getMonth());

  // State for the delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  useEffect(() => {
    const date = new Date();
    const monthObjects = [];
    const currentLocale = i18n.language === "ar" ? "ar-EG" : "en-US";
    for (let i = 0; i < 12; i++) {
      const tempDate = new Date(date.getFullYear(), i, 1);
      monthObjects.push({
        name: tempDate.toLocaleString(currentLocale, { month: "long" }),
        monthIndex: i,
      });
    }
    setMonths(monthObjects);
    setActiveMonth(date.getMonth());
  }, [i18n.language]);

  useEffect(() => {
    if (monthSliderRef.current) {
      const activeElement = monthSliderRef.current.querySelector(
        `.${styles.active}`
      );
      if (activeElement) {
        activeElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }, [activeMonth, months]);

  const fetchEmployeeFinancialData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setError(t("Authentication token not found."));
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const currentYear = new Date().getFullYear();
      const firstDay = new Date(currentYear, activeMonth, 1);
      const lastDay = new Date(currentYear, activeMonth + 1, 0);
      const fromTimestamp = firstDay.getTime();
      const toTimestamp = lastDay.getTime();
      const config = { headers: { Authorization: token } };

      const [employeeResponse, transactionsResponse] = await Promise.all([
        axios.get(
          `/api/emp/${employeeId}?from=${fromTimestamp}&to=${toTimestamp}`,
          config
        ),
        axios.get(
          `/api/payment/findByEmpId/${employeeId}?from=${fromTimestamp}&to=${toTimestamp}`,
          config
        ),
      ]);

      setEmployee(employeeResponse.data?.items?.[0] || null);
      setTransactions(transactionsResponse.data?.items || []);

      if (!employeeResponse.data?.items?.[0]) {
        setError(
          t("Employee financial data not found for the selected month.")
        );
      }
    } catch (err) {
      setError(t("Failed to fetch employee financial data."));
      toast.error(t("Failed to fetch employee financial data."));
    } finally {
      setLoading(false);
    }
  }, [employeeId, activeMonth, token, t]);

  useEffect(() => {
    fetchEmployeeFinancialData();
  }, [fetchEmployeeFinancialData]);

  // --- Handlers for Edit and Delete ---
  const handleEdit = (transaction) => {
    // CORRECTED: Pass the transaction object via navigation state
    navigate(
      `/employees/${employeeId}/transactions/${transaction.payment.id}/edit`,
      {
        state: {
          transactionToEdit: transaction,
          returnPath: `/employees/${employeeId}`,
        },
      } // Pass the whole object
    );
  };

  const handleDelete = (transaction) => {
    setTransactionToDelete(transaction);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!transactionToDelete) return;
    try {
      // API Call: DELETE /api/payment/{id}
      await axios.delete(`/api/payment/${transactionToDelete.payment.id}`, {
        headers: { Authorization: token },
      });
      toast.success(t("Transaction deleted successfully."));
      fetchEmployeeFinancialData(); // Refresh the data list
    } catch (error) {
      toast.error(t("Failed to delete transaction."));
      console.error("Delete error:", error);
    } finally {
      setIsDeleteModalOpen(false);
      setTransactionToDelete(null);
    }
  };

  const isRTL = i18n.language === "ar";
  const currency = userData?.Tenant?.currency || "EGP";

  return (
    <div className={styles.container}>
      <button
        onClick={() => navigate("/employees")}
        className={styles.backButton}
        aria-label={t("Go back")}
      >
        <FaArrowLeft style={{ transform: isRTL ? "rotate(180deg)" : "none" }} />
        <span>{t("Back to Employees")}</span>
      </button>

      {loading ? (
        <div className={styles.centeredMessage}>{t("Loading...")}</div>
      ) : error || !employee ? (
        <div className={styles.centeredMessage}>
          {error || t("No data available.")}
        </div>
      ) : (
        <>
          <header className={styles.profileHeader}>
            <div className={styles.employeeInfo}>
              <h1 className={styles.employeeName}>{employee.username}</h1>
              <div className={styles.employeeSubDetails}>
                <span>
                  <FaBriefcase /> {employee.role}
                </span>
                <span>
                  <FaEnvelope /> {employee.email || "N/A"}
                </span>
              </div>
            </div>
            <div className={styles.summaryCards}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>{t("Paid Salary")}</span>
                <span className={`${styles.summaryValue} ${styles.paid}`}>
                  {employee.paidSalary?.toLocaleString(i18n.language) || "0.00"}{" "}
                  {currency}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>
                  {t("Current Balance")}
                </span>
                <span className={`${styles.summaryValue} ${styles.balance}`}>
                  {employee.currentBalance?.toLocaleString(i18n.language) ||
                    "0.00"}{" "}
                  {currency}
                </span>
              </div>
            </div>
          </header>

          <div className={styles.controlsAndBreakdown}>
            <div className={styles.monthSliderContainer}>
              <div
                className={styles.horizontalScrollContainer}
                ref={monthSliderRef}
              >
                {months.map((month) => (
                  <button
                    key={month.monthIndex}
                    className={`${styles.monthTab} ${
                      activeMonth === month.monthIndex ? styles.active : ""
                    }`}
                    onClick={() => setActiveMonth(month.monthIndex)}
                  >
                    {month.name}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.financialGrid}>
              <div className={styles.gridItem}>
                <span className={styles.gridLabel}>{t("Basic Salary")}</span>
                <span className={styles.gridValue}>
                  {employee.basicSalary?.toLocaleString(i18n.language) ||
                    "0.00"}{" "}
                  {currency}
                </span>
              </div>
              <div className={styles.gridItem}>
                <span className={styles.gridLabel}>{t("Deductions")}</span>
                <span className={styles.gridValue}>
                  {employee.deductions?.toLocaleString(i18n.language) || "0.00"}{" "}
                  {currency}
                </span>
              </div>
              <div className={styles.gridItem}>
                <span className={styles.gridLabel}>{t("Advances")}</span>
                <span className={styles.gridValue}>
                  {employee.advances?.toLocaleString(i18n.language) || "0.00"}{" "}
                  {currency}
                </span>
              </div>
              <div className={styles.gridItem}>
                <span className={styles.gridLabel}>
                  {t("Remaining Advances Due")}
                </span>
                <span className={`${styles.gridValue} ${styles.outstanding}`}>
                  {employee.totalOutstandingAdvances?.toLocaleString(
                    i18n.language
                  ) || "0.00"}{" "}
                  {currency}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.transactionsSection}>
            <div className={styles.transactionsHeader}>
              <h2 className={styles.transactionsTitle}>
                {t("Salary Transactions for")}{" "}
                {months.find((m) => m.monthIndex === activeMonth)?.name}
              </h2>
              <button
                className={styles.addTransactionButton}
                onClick={() =>
                  navigate(`/employees/${employeeId}/transactions/new`)
                }
              >
                <FaPlus /> {t("Add Salary Transaction")}
              </button>
            </div>
            <div className={styles.transactionsContent}>
              {transactions.length > 0 ? (
                <div className={styles.transactionList}>
                  {transactions.map((tx) => (
                    <TransactionItem
                      key={tx.payment.id}
                      transaction={tx}
                      isRTL={isRTL}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              ) : (
                <div className={styles.noTransactionsContainer}>
                  <FaFileInvoiceDollar className={styles.noTransactionsIcon} />
                  <p>{t("No transactions recorded for this month.")}</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        transaction={transactionToDelete?.payment}
      />
    </div>
  );
};

export default EmployeeProfile;
